import { Campaign } from "model/campaign";
import { CampaignStatus } from "type/campaign-status";
import { DynamicContentType } from "type/dynamic-content";

/**
 * MAX-4201 — Market-required activation guardrail.
 *
 * A campaign whose Smart Content Source is Market cannot be activated until a Market is chosen;
 * its Market-based smart content has nothing to draw from and fails at send time. Saving is never
 * blocked — only activation. Kept pure so every surface (list toggle, Add Campaign, Settings,
 * Duplicate) reads the same rule, and so it can be unit-tested directly.
 *
 * `campaignType` is tri-state: Lead Profile, Market, or undefined (No Smart Content). Only Market
 * with no Market selected is blocked. Compared by id, not instance, so a type rebuilt from an API
 * payload still matches.
 */
export const isMarketMissing = (
	campaignType: DynamicContentType | undefined,
	marketId: number | null | undefined,
): boolean => campaignType?.id === DynamicContentType.MARKET.id && marketId == null;

/** True when this campaign cannot be activated until a Market is selected. */
export const isCampaignActivationBlocked = (campaign: Pick<Campaign, "campaignType" | "marketId">): boolean =>
	isMarketMissing(campaign.campaignType, campaign.marketId);

/**
 * Status for a duplicate of `campaign`. A copy of a blocked campaign is created Inactive — copying
 * an Active status would ask the server to create an active campaign with no Market, which it
 * rejects.
 */
export const duplicateStatus = (campaign: Campaign): CampaignStatus | undefined =>
	isCampaignActivationBlocked(campaign) ? CampaignStatus.INACTIVE : campaign.status;
